<template>
  <div class="relation-tree">
    <div v-for="node in childNodes" :key="node.id" class="relation-tree__branch">
      <div
        class="relation-tree__card"
        :class="{
          'relation-tree__card--root': node.isRoot,
          'relation-tree__card--scalar': node.isScalar,
          'relation-tree__card--warn': node.shiftsGrain,
          'relation-tree__card--active': node.id === activeNodeId,
        }">
        <div class="relation-tree__head">
          <v-icon small class="relation-tree__icon" :class="{ 'relation-tree__icon--warn': node.shiftsGrain }">
            {{ node.icon }}
          </v-icon>

          <div class="relation-tree__text">
            <div class="relation-tree__name-row">
              <span class="relation-tree__name">{{ node.label }}</span>
              <span v-if="node.isRoot" class="relation-tree__chip">{{ copy.RootBadge }}</span>
              <span v-else class="relation-tree__chip">{{ node.strategyChip }}</span>
            </div>
            <div class="relation-tree__detail">{{ node.detail }}</div>
          </div>

          <div class="relation-tree__actions">
            <button
              v-if="node.canConfigure"
              type="button"
              class="relation-tree__icon-button"
              :title="copy.ConfigureRelation"
              :aria-label="copy.ConfigureRelation"
              @click="requestConfigure(node, $event)">
              <v-icon small>mdi-tune-variant</v-icon>
            </button>
            <button
              v-if="node.canRemove"
              type="button"
              class="relation-tree__icon-button"
              :title="copy.RemoveRelation"
              :aria-label="copy.RemoveRelation"
              @click="$emit('remove', node.id)">
              <v-icon small>mdi-trash-can-outline</v-icon>
            </button>
          </div>
        </div>

        <!-- A count/exists relation IS a column: nothing hangs off it, so it has no children. -->
        <p v-if="node.isScalar" class="relation-tree__scalar">
          {{ copy.ScalarNodeNote }}: <b>{{ node.label }}</b>
        </p>

        <div v-else class="relation-tree__kids">
          <relation-tree
            :nodes="nodes"
            :parent-id="node.id"
            :active-node-id="activeNodeId"
            v-on="$listeners" />

          <button
            v-if="node.canAdd"
            type="button"
            class="relation-tree__add"
            @click="requestAdd(node, $event)">
            <v-icon small class="relation-tree__add-icon">mdi-plus</v-icon>
            {{ copy.AddRelation }}
          </button>

          <p v-else-if="node.addNote" class="relation-tree__note">{{ node.addNote }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { log } from '@/utils/debugLogger';
  import { RelationsCopy } from './relationsCopy';

  /**
   * The definition as a nested list: one card per node, children indented under their parent.
   *
   * The component is recursive — each level renders the same component for its children and
   * forwards its listeners, so the events a card raises reach the step no matter how deep the card
   * sits. It renders the view nodes the step prepared and mutates nothing; the anchor rectangle it
   * reports with an add or configure request is what the step positions the popover against.
   */
  export default {
    name: 'RelationTree',

    props: {
      /** Every view node in the definition, flat. Each level filters by `parentId` itself. */
      nodes: { type: Array, required: true },
      /** Which level this instance draws. `null` is the outermost, which holds the root node. */
      parentId: { type: String, default: null },
      /** The node whose popover is open, so its card can show as the one being worked on. */
      activeNodeId: { type: String, default: null },
    },

    data() {
      return { copy: RelationsCopy };
    },

    computed: {
      childNodes() {
        return this.nodes.filter((node) => (node.parentId || null) === this.parentId);
      },
    },

    methods: {
      requestAdd(node, event) {
        log('[RelationTree] add relation requested', { nodeId: node.id });
        this.$emit('add', { nodeId: node.id, rect: this.anchorOf(event) });
      },

      requestConfigure(node, event) {
        log('[RelationTree] configure relation requested', { nodeId: node.id });
        this.$emit('configure', { nodeId: node.id, rect: this.anchorOf(event) });
      },

      /** Viewport box of the control that was clicked — the step turns it into its own coordinates. */
      anchorOf(event) {
        const element = event && event.currentTarget;
        return element ? element.getBoundingClientRect() : null;
      },
    },
  };
</script>

<style scoped>
  .relation-tree__card {
    margin-bottom: 10px;
    border: 1px solid var(--rel-border-strong, #c7cdcb);
    border-radius: 12px;
    background: var(--rel-surface, #fcfdfc);
  }

  .relation-tree__card--root {
    border-color: var(--rel-primary, #03624c);
  }

  .relation-tree__card--scalar {
    border-style: dashed;
    background: var(--rel-surface-sunken, #f6f8f7);
  }

  .relation-tree__card--warn {
    border-color: var(--rel-warn-line, #c77d00);
  }

  .relation-tree__card--active {
    box-shadow: 0 2px 10px rgba(2, 26, 26, 0.14);
  }

  .relation-tree__head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 14px;
  }

  .relation-tree__icon {
    color: var(--rel-primary, #03624c);
  }

  .relation-tree__icon--warn {
    color: var(--rel-warn-line, #c77d00);
  }

  .relation-tree__text {
    min-width: 0;
  }

  .relation-tree__name-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .relation-tree__name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--rel-ink, #182420);
  }

  .relation-tree__chip {
    padding: 2px 7px;
    border-radius: 4px;
    background: var(--rel-primary-soft, rgba(3, 98, 76, 0.08));
    font-size: 11px;
    font-weight: 600;
    color: var(--rel-primary, #03624c);
    white-space: nowrap;
  }

  .relation-tree__detail {
    font-size: 11.5px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-tree__actions {
    display: flex;
    gap: 2px;
    align-items: center;
    margin-left: auto;
  }

  .relation-tree__icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-tree__icon-button:hover {
    background: var(--rel-primary-soft, rgba(3, 98, 76, 0.08));
    color: var(--rel-primary, #03624c);
  }

  .relation-tree__scalar {
    margin: 0;
    padding: 0 14px 10px;
    font-size: 11.5px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-tree__kids {
    padding: 0 0 8px 26px;
    margin-right: 8px;
  }

  .relation-tree__add {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin: 2px 0 10px;
    padding: 7px 10px;
    border: 1px dashed var(--rel-border-strong, #c7cdcb);
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--rel-primary, #03624c);
  }

  .relation-tree__add:hover {
    border-color: var(--rel-primary, #03624c);
    background: var(--rel-primary-soft, rgba(3, 98, 76, 0.08));
  }

  .relation-tree__add-icon {
    color: var(--rel-primary, #03624c);
  }

  .relation-tree__note {
    margin: 2px 0 10px;
    font-size: 11.5px;
    color: var(--rel-ink-muted, #5c6b66);
  }
</style>
